import { Fragment, useEffect } from "react"

// ** Third Party Components
import { z } from "zod"
import { Loader } from "lucide-react"
import { useForm } from "react-hook-form"
import { CloseCircle } from "iconsax-reactjs"
import { useLocation } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { zodResolver } from "@hookform/resolvers/zod"

// ** UI Components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@pma/components/common/form"

// ** Services
import { createNewProjectGroupService } from "@pma/services/project"

// ** Store
import { useAuth } from "@pma/stores/auth-store"
import { useCommonStore } from "@pma/stores/common-store"

// ** Types
import { DictType, ProjectGroupDialogProps } from "@pma/types"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pma/components/common/select"

const projectSchema = z.object({
  name: z.string().min(1, "Project group name is required"),
  organization_id: z.number(),
  parent_group_id: z.string(),
})

type ProjectGroupFormValues = z.infer<typeof projectSchema>

const ProjectGroupDialog = ({
  isMobile = false,
  onClose,
  refetchProjectGroupList,
}: ProjectGroupDialogProps) => {
  const location = useLocation()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const path = location.pathname.split("/")
  const orgId = authConfig?.last_organization_id

  const { projectGroupList, setProjectUpdateAction } = useCommonStore()

  const form = useForm<ProjectGroupFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: "",
      parent_group_id: "",
      organization_id: orgId,
    },
  })

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form

  useEffect(() => {
    if (path.includes("project-group")) {
      const groupId = path[path.length - 1]
      setValue("parent_group_id", groupId)
    }
  }, [path])

  const onSubmit = (data: ProjectGroupFormValues) => createProjectGroupMutation.mutate(data)

  const createProjectGroupMutation = useMutation({
    mutationKey: ["create-new-project-group"],
    mutationFn: createNewProjectGroupService,
    onSuccess: (data) => {
      onClose()
      refetchProjectGroupList()
      setProjectUpdateAction(true)
      toast({
        title: translate("Success"),
        description: `${data?.title} ${translate("project group created successfully")}`,
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to create project"),
        variant: "error",
      })
    },
  })

  return (
    <Fragment>
      {/* Header */}
      {!isMobile ? (
        <div className='flex items-center justify-between border-b border-neutral-200 px-5 py-4'>
          <h2 className='font-[inter] text-base font-medium text-neutral-800'>
            {translate("New Project Group")}
          </h2>
          <CloseCircle className='cursor-pointer text-neutral-800' size={24} onClick={onClose} />
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={cn("flex flex-col gap-3 overflow-scroll border-b", isMobile ? "p-3" : "p-6")}
            style={isMobile ? {} : { maxHeight: "calc(100vh - 320px)" }}
          >
            <FormField
              control={control}
              name='parent_group_id'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate("Parent Group Name")}:</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                        <SelectValue placeholder={translate("Select Parent Group")} />
                      </SelectTrigger>
                      <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                        {projectGroupList?.map((group: any, index: number) => (
                          <SelectItem value={String(group?.id)} className='rounded-lg' key={index}>
                            {translate(group.name)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {translate("Project Group Name")}: <small className='text-red-500'>*</small>
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      className={cn(
                        "w-full rounded-lg border px-3 py-2 text-sm outline-none",
                        errors?.name
                          ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                          : "border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                      )}
                      placeholder={translate("Enter project group name")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn("flex items-center justify-end gap-3", isMobile ? "p-3" : "p-6")}>
            <button
              className='rounded-full border border-neutral-200 px-6 py-2 font-[inter] text-sm font-normal text-neutral-800'
              type='button'
              onClick={onClose}
            >
              {translate("Cancel")}
            </button>
            <button
              className='flex rounded-full bg-indigo-600 px-6 py-2 font-[inter] text-sm font-medium text-white shadow'
              type='submit'
              disabled={createProjectGroupMutation.isPending}
              style={{
                background:
                  "linear-gradient(0deg, var(--Indigo-8, #444CE7), var(--Indigo-8, #444CE7)), linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
              }}
            >
              {createProjectGroupMutation.isPending ? (
                <Loader className={cn("animate-spin", isHebrew ? "ml-1" : "mr-1")} size={16} />
              ) : null}{" "}
              {translate("Save")}
            </button>
          </div>
        </form>
      </Form>
    </Fragment>
  )
}

export default ProjectGroupDialog
